import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ArrowRight, Calendar, Clock, Share2, Twitter, Linkedin, Facebook } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { blogPosts, findPostBySlug } from "@/i18n/blogPosts";
import ScrollReveal from "@/components/ScrollReveal";

const BlogPost = ({ onOpenAudit }) => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const post = findPostBySlug(slug);
  const [activeId, setActiveId] = useState(null);

  // Find prev / next
  const idx = post ? blogPosts.findIndex((p) => p.slug === slug) : -1;
  const prev = idx > 0 ? blogPosts[idx - 1] : null;
  const next = idx >= 0 && idx < blogPosts.length - 1 ? blogPosts[idx + 1] : null;
  const related = post ? blogPosts.filter((p) => p.slug !== slug).slice(0, 2) : [];

  useEffect(() => {
    if (!post) return;
    const onScroll = () => {
      const headings = document.querySelectorAll("[data-section-id]");
      let current = null;
      headings.forEach((h) => {
        const top = h.getBoundingClientRect().top;
        if (top < 200) current = h.getAttribute("data-section-id");
      });
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const tocItems = post.toc[lang];
  const body = post.body[lang];
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <main data-testid="blog-post-page" className="bg-brand-bg text-white pt-32">
      {/* Header */}
      <section className="relative border-b border-brand-border overflow-hidden">
        <div className="aurora opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-[1100px] px-6 lg:px-10 py-12 md:py-20">
          <Link
            to="/blog"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-zinc-400 hover:text-brand-purpleLight transition-colors mb-10"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.nav.blog}
          </Link>

          <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400 mb-8">
            <span className="px-3 py-1 rounded-full border border-brand-purple/40 text-brand-purpleLight">
              {post.cat[lang]}
            </span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" />{post.date}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3" />{post.time[lang]}</span>
          </div>

          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tighter mb-10">
            {post.title[lang]}
          </h1>

        </div>
      </section>

      {/* Hero image */}
      <div className="border-b border-brand-border">
        <img src={post.image} alt={post.title[lang]} className="w-full h-[40vh] md:h-[60vh] object-cover" />
      </div>

      {/* Body + sticky sidebar */}
      <section className="border-b border-brand-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10">
          {/* Article body */}
          <article className="lg:col-span-8 max-w-3xl">
            <p className="text-lg md:text-xl text-zinc-200 leading-relaxed mb-10 border-l-2 border-brand-purple pl-6">
              {post.intro[lang]}
            </p>

            <div className="space-y-8">
              {body.map((b, i) => {
                if (b.type === "h2") {
                  return (
                    <h2
                      key={i}
                      data-section-id={b.id}
                      id={`s-${b.id}`}
                      className="scroll-mt-32 font-heading text-3xl md:text-4xl font-black uppercase tracking-tight pt-4"
                    >
                      {b.text}
                    </h2>
                  );
                }
                if (b.type === "p") {
                  return (
                    <p key={i} className="text-zinc-300 leading-relaxed text-base md:text-lg">
                      {b.text}
                    </p>
                  );
                }
                if (b.type === "ul") {
                  return (
                    <ul key={i} className="space-y-3">
                      {b.items.map((it, j) => (
                        <li key={j} className="flex items-start gap-3 text-zinc-200">
                          <span className="mt-[10px] w-1.5 h-1.5 rounded-full bg-brand-purpleLight shrink-0" />
                          {it}
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (b.type === "callout") {
                  return (
                    <div key={i} className="holo-card rounded-2xl bg-brand-surface/60 p-6 md:p-8">
                      <p className="text-brand-purpleLight font-mono text-[10px] uppercase tracking-[0.25em] mb-3">
                        {lang === "bg" ? "Pro tip" : "Pro tip"}
                      </p>
                      <p className="text-white text-base md:text-lg leading-relaxed">{b.text}</p>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            {/* Bottom inline CTA */}
            <div className="mt-16 rounded-2xl border border-brand-border bg-brand-surface/60 p-8 md:p-10 holo-card">
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-3">
                {lang === "bg" ? "АКЦИЯ — БЕЗПЛАТЕН AUDIT" : "ACTION — FREE AUDIT"}
              </p>
              <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-4">
                {lang === "bg"
                  ? "Искаш персонална стратегия за твоя бранд?"
                  : "Want a personal strategy for your brand?"}
              </h3>
              <p className="text-zinc-300 mb-6">
                {lang === "bg"
                  ? "Стартирай безплатен 5-минутен AI audit. Получаваш реален отчет, не общи приказки."
                  : "Run a free 5-minute AI audit. You get a real report, not generic talk."}
              </p>
              <button
                data-testid="blog-post-audit"
                onClick={onOpenAudit}
                className="btn-holo inline-flex items-center gap-2 font-bold uppercase tracking-wide px-7 py-4 rounded-full text-xs"
              >
                {lang === "bg" ? "Стартирай audit" : "Run the audit"}
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
          </article>

          {/* Sticky sidebar */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-8">
              {/* TOC */}
              <div data-testid="blog-toc" className="rounded-2xl border border-brand-border bg-brand-surface/60 p-6">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-4">
                  {lang === "bg" ? "В тази статия" : "On this page"}
                </p>
                <ul className="space-y-2">
                  {tocItems.map((toc) => {
                    const active = activeId === toc.id;
                    return (
                      <li key={toc.id}>
                        <a
                          href={`#s-${toc.id}`}
                          data-testid={`blog-toc-link-${toc.id}`}
                          className={`block text-sm py-1.5 transition-colors ${
                            active
                              ? "text-brand-purpleLight font-medium"
                              : "text-zinc-400 hover:text-white"
                          }`}
                        >
                          <span className={`inline-block w-1 h-3 rounded-full mr-2 align-middle ${active ? "bg-brand-pink" : "bg-brand-border"}`} />
                          {toc.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Share */}
              <div className="rounded-2xl border border-brand-border bg-brand-surface/60 p-6">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-4 inline-flex items-center gap-2">
                  <Share2 className="w-3 h-3" /> {lang === "bg" ? "Сподели" : "Share"}
                </p>
                <div className="flex items-center gap-2">
                  {[
                    { Icon: Twitter, href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title[lang])}` },
                    { Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
                    { Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
                  ].map(({ Icon, href }, k) => (
                    <a
                      key={k}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`blog-share-${k}`}
                      className="w-10 h-10 grid place-items-center rounded-full border border-brand-border hover:border-brand-purple hover:text-brand-purpleLight transition-colors text-zinc-300"
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Prev / Next nav */}
      <section className="border-b border-brand-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10 grid md:grid-cols-2 gap-px bg-brand-border">
          {prev ? (
            <Link
              to={`/blog/${prev.slug}`}
              data-testid="blog-prev"
              className="group bg-brand-bg p-8 hover:bg-brand-surface transition-colors flex items-start gap-5"
            >
              <ArrowLeft className="w-5 h-5 mt-1 text-brand-purpleLight group-hover:-translate-x-1 transition-transform" />
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">
                  {lang === "bg" ? "Предишна статия" : "Previous article"}
                </p>
                <p className="font-heading text-lg md:text-xl font-bold leading-tight">
                  {prev.title[lang]}
                </p>
              </div>
            </Link>
          ) : (
            <Link
              to="/blog"
              data-testid="blog-back-to-list"
              className="group bg-brand-bg p-8 hover:bg-brand-surface transition-colors flex items-start gap-5"
            >
              <ArrowLeft className="w-5 h-5 mt-1 text-brand-purpleLight group-hover:-translate-x-1 transition-transform" />
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">
                  {lang === "bg" ? "Обратно към блога" : "Back to journal"}
                </p>
                <p className="font-heading text-lg md:text-xl font-bold leading-tight">
                  {lang === "bg" ? "Виж всички статии" : "Browse all articles"}
                </p>
              </div>
            </Link>
          )}
          {next ? (
            <Link
              to={`/blog/${next.slug}`}
              data-testid="blog-next"
              className="group bg-brand-bg p-8 hover:bg-brand-surface transition-colors flex items-start gap-5 md:text-right md:justify-end"
            >
              <div className="md:order-1">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">
                  {lang === "bg" ? "Следваща статия" : "Next article"}
                </p>
                <p className="font-heading text-lg md:text-xl font-bold leading-tight">
                  {next.title[lang]}
                </p>
              </div>
              <ArrowRight className="w-5 h-5 mt-1 text-brand-purpleLight group-hover:translate-x-1 transition-transform md:order-2" />
            </Link>
          ) : (
            <Link
              to="/blog"
              data-testid="blog-back-to-list"
              className="group bg-brand-bg p-8 hover:bg-brand-surface transition-colors flex items-start gap-5 md:text-right md:justify-end"
            >
              <div className="md:order-1">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">
                  {lang === "bg" ? "Обратно към блога" : "Back to journal"}
                </p>
                <p className="font-heading text-lg md:text-xl font-bold leading-tight">
                  {lang === "bg" ? "Виж всички статии" : "Browse all articles"}
                </p>
              </div>
              <ArrowRight className="w-5 h-5 mt-1 text-brand-purpleLight group-hover:translate-x-1 transition-transform md:order-2" />
            </Link>
          )}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="border-b border-brand-border">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-6">
              {lang === "bg" ? "Свързани статии" : "Related"}
            </p>
            <h3 className="font-heading text-3xl md:text-5xl font-black uppercase tracking-tighter mb-12">
              {lang === "bg" ? (
                <>Продължи <span className="text-white">четенето</span></>
              ) : (
                <>Keep <span className="text-white">reading</span></>
              )}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {related.map((p, i) => (
                <ScrollReveal
                  key={p.slug}
                  delay={i * 80}
                  as="article"
                  className="group rounded-2xl border border-brand-border bg-brand-surface/60 hover:bg-brand-surface transition-colors bento-cell card-glow overflow-hidden"
                >
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-related-${p.slug}`} className="block">
                    <div className="aspect-[16/10] overflow-hidden relative">
                      <img src={p.image} alt={p.title[lang]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-6 md:p-8">
                      <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-4">
                        <span className="text-brand-purpleLight">{p.cat[lang]}</span>
                        <span>•</span>
                        <span>{p.time[lang]}</span>
                      </div>
                      <h3 className="font-heading text-xl md:text-2xl font-bold leading-snug">
                        {p.title[lang]}
                      </h3>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default BlogPost;
